import { useId } from 'react';
import { ribbonPath, type Point, type StagePosition } from './geometry';
import type { RoadmapLevelStage } from 'Selectors/curriculum';

/** Projected top, bevel and vertical faces share geometry; controls stay upright HTML. */
export function Ribbon({
  points,
  graph,
  width,
  height,
  platforms,
}: {
  points: Point[];
  graph: RoadmapLevelStage[];
  width: number;
  height: number;
  platforms: Map<number, StagePosition>;
}) {
  const id = useId().replace(/:/g, '');
  const path = ribbonPath(points);
  return (
    <svg className="rm-ribbon" width={width} height={height} aria-hidden="true">
      <defs>
        <linearGradient id={`${id}-road`} x1="0" y1="0" x2="0.25" y2="1">
          <stop stopColor="#5a7183" />
          <stop offset=".22" stopColor="#344e63" />
          <stop offset=".72" stopColor="#21394d" />
          <stop offset="1" stopColor="#182f43" />
        </linearGradient>
        <linearGradient id={`${id}-edge`} x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#294356" />
          <stop offset=".55" stopColor="#142b3c" />
          <stop offset="1" stopColor="#071625" />
        </linearGradient>
        <linearGradient id={`${id}-bridge`} x1="0" y1="0" x2="1" y2="0">
          <stop stopColor="#284354" />
          <stop offset=".4" stopColor="#466676" />
          <stop offset="1" stopColor="#1c3949" />
        </linearGradient>
      </defs>
      {/* The bridges leave the underside of the road and meet the rear dock of each stage. */}
      {points.map((p, i) => {
        const branch = graph[i].repertoire;
        if (!branch) return null;
        const platform = platforms.get(graph[i].levelNumber);
        const dock = platform ? platform.y + 28 : p.y + 142;
        const bridge = `M ${p.x} ${p.y + 20} C ${p.x} ${p.y + 82}, ${p.x + 14} ${dock - 45}, ${p.x} ${dock}`;
        return (
          <g
            key={graph[i].levelNumber}
            className={`rm-bridge ${branch.availability === 'future' ? 'is-future' : ''}`}
          >
            <path
              d={bridge}
              stroke="#010914"
              strokeWidth="47"
              transform="translate(6 18)"
              opacity=".65"
            />
            <path d={bridge} stroke="#122738" strokeWidth="40" transform="translate(0 11)" />
            <path d={bridge} stroke="#76919e" strokeWidth="40" />
            <path d={bridge} stroke={`url(#${id}-bridge)`} strokeWidth="37" />
            {[-14, 14].map((offset) => (
              <path
                key={offset}
                d={bridge}
                transform={`translate(${offset} 0)`}
                stroke={branch.availability === 'future' ? '#6f869c' : '#6dcbb0'}
                strokeWidth="1.3"
                opacity=".8"
              />
            ))}
            {Array.from({ length: Math.max(0, Math.floor((dock - p.y - 58) / 17)) }, (_, j) => (
              <path
                key={j}
                d={`M ${p.x - 11} ${p.y + 73 + j * 17} h 23`}
                stroke="#9bb3ba"
                strokeWidth="1"
                opacity=".27"
              />
            ))}
          </g>
        );
      })}
      {/* The broad lower face makes the ribbon a solid structure, with a separate contact shadow. */}
      <path d={path} transform="translate(9 43)" stroke="#010812" strokeWidth="88" opacity=".45" />
      <path d={path} transform="translate(0 30)" stroke="#091b2a" strokeWidth="80" />
      <path d={path} transform="translate(0 24)" stroke={`url(#${id}-edge)`} strokeWidth="80" />
      <path d={path} transform="translate(0 12)" stroke="#3c586b" strokeWidth="81" />
      <path d={path} transform="translate(0 8)" stroke="#172e40" strokeWidth="81" />
      <path d={path} stroke="#8ca6b4" strokeWidth="82" />
      <path d={path} stroke={`url(#${id}-road)`} strokeWidth="78" />
      {points.slice(1).map(
        (p, i) =>
          graph[i].lessonState === 'complete' &&
          graph[i + 1].lessonState === 'complete' && (
            <g key={graph[i].levelNumber}>
              <path
                d={ribbonPath([points[i], p])}
                stroke="#53ad93"
                strokeWidth="68"
                opacity=".16"
              />
              {[-30, 30].map((offset) => (
                <path
                  key={offset}
                  d={ribbonPath([points[i], p])}
                  transform={`translate(0 ${offset})`}
                  stroke="#8ae1be"
                  strokeWidth="1.5"
                  opacity=".7"
                />
              ))}
            </g>
          ),
      )}
      {[-35, -15, 0, 15, 35].map((offset) => (
        <path
          key={offset}
          d={path}
          transform={`translate(0 ${offset})`}
          stroke="#bdcfd6"
          strokeOpacity={offset === -35 ? '.65' : '.12'}
          strokeWidth={offset === -35 ? '1.5' : '1'}
        />
      ))}
      {points.map((p, i) => (
        <g key={graph[i].levelNumber}>
          <ellipse
            cx={p.x + 5}
            cy={p.y + 14}
            rx="56"
            ry="28"
            fill="#020c18"
            opacity=".5"
            stroke="none"
          />
          <ellipse
            cx={p.x}
            cy={p.y + 5}
            rx="54"
            ry="31"
            fill="#152d3b"
            stroke="#6a8b98"
            strokeWidth="1"
          />
          <ellipse
            cx={p.x}
            cy={p.y + 2}
            rx="49"
            ry="27"
            fill="#203c49"
            stroke="#a0bbc3"
            strokeOpacity=".28"
          />
        </g>
      ))}
    </svg>
  );
}

/** Rounded outline in the fallback artwork's 720 × 290 projection. */
const panel = (left: number, top: number, right: number, bottom: number, rx: number, ry: number) =>
  `M ${left + rx} ${top} H ${right - rx} A ${rx} ${ry} 0 0 1 ${right} ${top + ry} ` +
  `V ${bottom - ry} A ${rx} ${ry} 0 0 1 ${right - rx} ${bottom} H ${left + rx} ` +
  `A ${rx} ${ry} 0 0 1 ${left} ${bottom - ry} V ${top + ry} A ${rx} ${ry} 0 0 1 ${left + rx} ${top} Z`;

/**
 * Flat stand-in for the modeled platform when WebGL is unavailable. Its wall, deck,
 * front face and lamps share the overlay rows projected from the 3D kit.
 */
export function StageArtwork() {
  const id = useId().replace(/:/g, '');
  const deck = panel(23, 43, 697, 217, 123, 66);
  return (
    <svg
      className="rm-stage-art"
      viewBox="0 0 720 290"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${id}-floor`} x1="0" y1="0" x2=".3" y2="1">
          <stop stopColor="#3f6070" />
          <stop offset=".35" stopColor="#24435a" />
          <stop offset="1" stopColor="#132d42" />
        </linearGradient>
        <linearGradient id={`${id}-front`} x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#2a4757" />
          <stop offset=".35" stopColor="#163343" />
          <stop offset="1" stopColor="#081c2c" />
        </linearGradient>
        <radialGradient id={`${id}-light`}>
          <stop stopColor="#f6e3b0" stopOpacity=".16" />
          <stop offset="1" stopColor="#f6e3b0" stopOpacity="0" />
        </radialGradient>
        <clipPath id={`${id}-clip`}>
          <path d={deck} />
        </clipPath>
      </defs>
      <path d={panel(18, 112, 714, 290, 128, 70)} fill="#010b16" opacity=".55" />
      <path d={panel(10, 105, 710, 283, 130, 70)} fill="#081a28" stroke="#2c4654" />
      <path d={panel(15, 81, 705, 257, 126, 68)} fill={`url(#${id}-front)`} stroke="#45697a" />
      <path
        d={panel(23, 71, 697, 245, 123, 66)}
        fill={`url(#${id}-front)`}
        stroke="#87d7ba"
        strokeWidth="2.5"
      />
      <path d={deck} fill={`url(#${id}-floor)`} stroke="#90b2bb" strokeWidth="1.5" />
      <g clipPath={`url(#${id}-clip)`}>
        {[78, 113, 148, 183].map((y) => (
          <path key={y} d={`M 0 ${y} H 720`} stroke="#94b8c0" strokeOpacity=".1" />
        ))}
        {[205, 515].map((x) => (
          <path key={x} d={`M ${x} 43 V 217`} stroke="#94b8c0" strokeOpacity=".1" />
        ))}
        {[205, 360, 515].map((x) => (
          <ellipse key={x} cx={x} cy="104" rx="78" ry="42" fill={`url(#${id}-light)`} />
        ))}
        <path
          d={panel(37, 51, 683, 209, 112, 60)}
          fill="none"
          stroke="#8cb5bf"
          strokeOpacity=".28"
        />
      </g>
      {/* The upright backdrop carries the heading; staff engraving flanks it. */}
      <rect x="77" y="8" width="566" height="62" rx="11" fill="#13293a" stroke="#577e8b" />
      <path d="M 86 10 H 634" stroke="#93b5bc" strokeWidth="2" strokeLinecap="round" />
      {[37, 42.5, 48, 53.5, 59].map((y) => (
        <path key={y} d={`M 92 ${y} H 214 M 506 ${y} H 628`} stroke="#94b8c0" strokeOpacity=".2" />
      ))}
      {[205, 360, 515].map((x) => (
        <g key={x}>
          <rect x={x - 9} y="67" width="18" height="8" rx="2" fill="#0f2434" stroke="#4b6f7d" />
          <ellipse cx={x} cy="77" rx="15" ry="4" fill="#f6e3b0" opacity=".16" />
          <ellipse cx={x} cy="73" rx="4" ry="1.8" fill="#fbf1d8" />
        </g>
      ))}
    </svg>
  );
}
